import { Button, Callout, Modal, Panel, Select, Text, Thumbnail, toast } from "@imagedesignai/neo";
import {
  type ChangeEvent,
  type FormEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GENERATION_SETTINGS_CONTRACT_VERSION } from "../../src/generation-settings.ts";
import {
  type GenerationConfigSurface,
  type GenerationConfigView,
  type GifmeApiClient,
  gifmeErrorMessage,
  type SelfieView,
  type SourceAssetView,
} from "./api-client.ts";
import { CameraSelfieModal } from "./camera-selfie-modal.tsx";
import type { MediaCache } from "./media-cache.ts";
import { useCachedMediaUrl, useNearViewport } from "./media-image.tsx";
import { appendUniqueSourceAssets } from "./source-pages.ts";
import {
  createLocalPreviewRegistry,
  createSubmissionKeyState,
  submitRunRequest,
} from "./submission-state.ts";

interface UploadedSelfie {
  kind: "upload";
  file: File;
  previewUrl: string;
}

interface SavedSelfie {
  kind: "saved";
  id: string;
}

type SelfieChoice = UploadedSelfie | SavedSelfie;

interface UploadedSource {
  kind: "upload";
  file: File;
  previewUrl: string;
}

interface SavedSource {
  kind: "saved";
  source: SourceAssetView;
  originalName: string;
}

type SourceChoice = UploadedSource | SavedSource;

export interface StudioPrefill {
  key: string;
  selfieId: string | null;
  sources: Array<{ source: SourceAssetView; originalName: string }>;
}

export interface StudioViewProps {
  api: GifmeApiClient;
  mediaCache: MediaCache;
  prefill: StudioPrefill | null;
  onQueued(): void | Promise<void>;
}

function dateLabel(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));
}

function SavedSelfieThumbnail({
  selfie,
  selected,
  cache,
  onSelect,
}: {
  selfie: SelfieView;
  selected: boolean;
  cache: MediaCache;
  onSelect(): void;
}) {
  const { targetRef, isNearViewport } = useNearViewport<HTMLDivElement>();
  const src = useCachedMediaUrl(cache, selfie.mediaUrl, isNearViewport);
  return (
    <div ref={targetRef} className="picker-item">
      <Thumbnail
        caption={selfie.originalName}
        src={src ?? undefined}
        loading={!src}
        selected={selected}
        onSelect={onSelect}
      />
    </div>
  );
}

function SourcePickerItem({
  source,
  cache,
  onSelect,
}: {
  source: SourceAssetView;
  cache: MediaCache;
  onSelect(): void;
}) {
  const { targetRef, isNearViewport } = useNearViewport<HTMLDivElement>();
  const src = useCachedMediaUrl(cache, source.mediaUrl, isNearViewport);
  return (
    <div ref={targetRef} className="picker-item">
      <Thumbnail
        caption={dateLabel(source.createdAt)}
        src={src ?? undefined}
        loading={!src}
        onSelect={onSelect}
      />
    </div>
  );
}

function SourcePicker({
  api,
  cache,
  onClose,
  onSelect,
}: {
  api: GifmeApiClient;
  cache: MediaCache;
  onClose(): void;
  onSelect(source: SourceAssetView): void;
}) {
  const [sources, setSources] = useState<SourceAssetView[]>([]);
  const [nextOffset, setNextOffset] = useState<number | null>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sentinel = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  const loadPage = useCallback(
    async (offset: number) => {
      if (isLoadingRef.current) return;
      isLoadingRef.current = true;
      setIsLoading(true);
      try {
        const payload = await api.json<{ sources: SourceAssetView[]; nextOffset: number | null }>(
          `/api/sources?offset=${offset}`,
        );
        setSources((current) => appendUniqueSourceAssets(current, payload.sources));
        setNextOffset(payload.nextOffset);
        setError(null);
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : String(reason));
      } finally {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    void loadPage(0);
  }, [loadPage]);

  useEffect(() => {
    const target = sentinel.current;
    if (!target || nextOffset === null) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void loadPage(nextOffset);
      },
      { rootMargin: "50%" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [loadPage, nextOffset]);

  return (
    <Modal
      title="Previous input GIFs"
      onClose={onClose}
      loading={isLoading && sources.length === 0}
    >
      <div className="picker-content">
        <Text kind="caption" tone="muted">
          Any loop the team has already cast can be reused — no re-upload.
        </Text>
        {error ? (
          <Callout intent="danger" label="Could not load previous GIFs">
            {error}
          </Callout>
        ) : null}
        <div className="thumbnail-grid picker-grid">
          {sources.map((source) => (
            <SourcePickerItem
              key={source.sourceGifId}
              source={source}
              cache={cache}
              onSelect={() => onSelect(source)}
            />
          ))}
        </div>
        {sources.length === 0 && !isLoading && !error ? (
          <Text kind="body" tone="secondary">
            No previous GIFs yet.
          </Text>
        ) : null}
        {nextOffset !== null ? <div ref={sentinel} className="picker-sentinel" /> : null}
      </div>
    </Modal>
  );
}

function SourceSlot({
  choice,
  cache,
  onUpload,
  onBrowse,
}: {
  choice: SourceChoice | null;
  cache: MediaCache;
  onUpload(file: File): void;
  onBrowse(): void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const savedPath = choice?.kind === "saved" ? choice.source.mediaUrl : null;
  const savedUrl = useCachedMediaUrl(cache, savedPath);
  const preview = choice?.kind === "upload" ? choice.previewUrl : savedUrl;
  const name =
    choice?.kind === "upload"
      ? choice.file.name
      : choice?.kind === "saved"
        ? choice.originalName
        : "choose a loop";

  const change = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (file) onUpload(file);
  };

  return (
    <section className="source-slot" aria-label="Input GIF">
      <div className="source-preview">
        {preview ? <img src={preview} alt="" /> : <span aria-hidden="true">GIF</span>}
      </div>
      <Text as="span" kind="caption" tone="muted" className="mono ellipsis">
        {name}
      </Text>
      <div className="source-slot-actions">
        <Button
          type="button"
          intent="ghost"
          size="sm"
          icon="upload"
          shape="rounded"
          aria-label="Upload input GIF"
          onClick={() => input.current?.click()}
        />
        <Button
          type="button"
          intent="ghost"
          size="sm"
          icon="arrow-right-left"
          shape="rounded"
          aria-label="Choose saved input GIF"
          onClick={onBrowse}
        />
      </div>
      <input
        ref={input}
        className="visually-hidden"
        type="file"
        accept="image/gif,.gif"
        aria-label="Upload input GIF"
        onChange={change}
      />
    </section>
  );
}

export function StudioView({ api, mediaCache, prefill, onQueued }: StudioViewProps) {
  const [selfies, setSelfies] = useState<SelfieView[]>([]);
  const [selfie, setSelfie] = useState<SelfieChoice | null>(null);
  const [source, setSource] = useState<SourceChoice | null>(null);
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generationConfigs, setGenerationConfigs] = useState<GenerationConfigView[]>([]);
  const [selectedGenerationConfigId, setSelectedGenerationConfigId] = useState<string | null>(null);
  const [generationConfigError, setGenerationConfigError] = useState<string | null>(null);
  const [isLoadingGenerationConfigs, setIsLoadingGenerationConfigs] = useState(true);
  const [status, setStatus] = useState("Pick a selfie and a source loop to cast.");
  const [error, setError] = useState<string | null>(null);
  const selfieInput = useRef<HTMLInputElement>(null);
  const currentSelfie = useRef<SelfieChoice | null>(null);
  const selfieIntentRevision = useRef(0);
  const cameraSaveSequence = useRef(0);
  const submissionKey = useMemo(() => createSubmissionKeyState(), []);
  const localPreviews = useMemo(() => createLocalPreviewRegistry(), []);
  const selectedGenerationConfig = useMemo(
    () => generationConfigs.find(({ id }) => id === selectedGenerationConfigId) ?? null,
    [generationConfigs, selectedGenerationConfigId],
  );
  const generationConfigFingerprint = selectedGenerationConfig
    ? `${selectedGenerationConfig.id}@${selectedGenerationConfig.revision}`
    : null;

  useEffect(() => () => localPreviews.clear(), [localPreviews]);
  useEffect(() => {
    if (generationConfigFingerprint !== null) submissionKey.invalidate();
  }, [generationConfigFingerprint, submissionKey]);

  const refreshSelfies = useCallback(async () => {
    const payload = await api.json<{ selfies: SelfieView[] }>("/api/selfies");
    setSelfies(payload.selfies);
  }, [api]);

  const loadGenerationConfigs = useCallback(async (): Promise<boolean> => {
    setIsLoadingGenerationConfigs(true);
    setGenerationConfigError(null);
    try {
      const payload = await api.json<GenerationConfigSurface>("/api/generation-configs");
      if (payload.contractVersion !== GENERATION_SETTINGS_CONTRACT_VERSION) {
        throw new Error(`Unsupported generation settings contract ${payload.contractVersion}`);
      }
      const initial = payload.configs.find(({ id }) => id === payload.registry.defaultConfigId);
      if (!initial) throw new Error("The default generation configuration is unavailable");
      setGenerationConfigs(payload.configs);
      setSelectedGenerationConfigId((current) =>
        current && payload.configs.some(({ id }) => id === current) ? current : initial.id,
      );
      return true;
    } catch (reason) {
      setGenerationConfigs([]);
      setSelectedGenerationConfigId(null);
      setGenerationConfigError(
        gifmeErrorMessage(reason, "Could not load generation configurations."),
      );
      return false;
    } finally {
      setIsLoadingGenerationConfigs(false);
    }
  }, [api]);

  const setSelfieIntent = useCallback(
    (next: SelfieChoice | null) => {
      if (currentSelfie.current?.kind === "upload") {
        localPreviews.release(currentSelfie.current.previewUrl);
      }
      currentSelfie.current = next;
      selfieIntentRevision.current += 1;
      submissionKey.invalidate();
      setSelfie(next);
    },
    [localPreviews, submissionKey],
  );

  useEffect(() => {
    void refreshSelfies().catch(() => toast.danger("Could not load saved selfies"));
  }, [refreshSelfies]);

  useEffect(() => {
    void loadGenerationConfigs();
  }, [loadGenerationConfigs]);

  useEffect(() => {
    if (!prefill) return;
    localPreviews.clear();
    setSelfieIntent(prefill.selfieId ? { kind: "saved", id: prefill.selfieId } : null);
    const firstSource = prefill.sources[0];
    setSource(
      firstSource
        ? { kind: "saved", source: firstSource.source, originalName: firstSource.originalName }
        : null,
    );
    setStatus(
      prefill.selfieId
        ? "Inputs restored. Review them before casting."
        : "Source restored. Choose your selfie before casting.",
    );
    setError(null);
  }, [localPreviews, prefill, setSelfieIntent]);

  const canSubmit = Boolean(selfie && source && selectedGenerationConfig);

  const saveCameraSelfie = async (file: File): Promise<SelfieView> => {
    const sequence = ++cameraSaveSequence.current;
    const startingRevision = selfieIntentRevision.current;
    const saved = await api.saveSelfie(file);
    setSelfies((current) => [saved, ...current.filter(({ id }) => id !== saved.id)]);
    if (
      sequence === cameraSaveSequence.current &&
      startingRevision === selfieIntentRevision.current
    ) {
      setSelfieIntent({ kind: "saved", id: saved.id });
    }
    return saved;
  };

  const uploadSelfie = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) return;
    setSelfieIntent({ kind: "upload", file, previewUrl: localPreviews.create(file) });
    setError(null);
  };

  const uploadSource = (file: File) => {
    if (source?.kind === "upload") localPreviews.release(source.previewUrl);
    submissionKey.invalidate();
    const previewUrl = localPreviews.create(file);
    setSource({ kind: "upload", file, previewUrl });
    setError(null);
  };

  const chooseSource = (next: SourceAssetView) => {
    if (source?.kind === "upload") localPreviews.release(source.previewUrl);
    submissionKey.invalidate();
    setSource({ kind: "saved", source: next, originalName: next.sourceOriginalName });
    setIsPickerOpen(false);
    setError(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit || !selfie || !source || !selectedGenerationConfig) {
      setError("Choose a selfie, input GIF, and generation configuration.");
      return;
    }
    setIsSubmitting(true);
    setError(null);
    setStatus("Starting…");
    const body = new FormData();
    if (selfie.kind === "upload") body.set("selfie", selfie.file);
    else body.set("selfieId", selfie.id);
    if (source.kind === "upload") body.set("source", source.file);
    else body.set("sourceGifId", source.source.sourceGifId);
    body.set("generationConfigId", selectedGenerationConfig.id);
    body.set("generationConfigRevision", String(selectedGenerationConfig.revision));
    try {
      try {
        await submitRunRequest(api.request, body, submissionKey);
      } catch (reason) {
        const message = reason instanceof Error ? reason.message : String(reason);
        setStatus("Cast was not queued.");
        const didReload =
          message === "generation_config_revision_conflict" ||
          message === "generation_config_not_found"
            ? await loadGenerationConfigs()
            : false;
        if (didReload && message === "generation_config_revision_conflict") {
          setError("This configuration changed elsewhere. The latest version has been loaded.");
        } else if (didReload && message === "generation_config_not_found") {
          setError(
            "The selected configuration is no longer available. The current default has been selected.",
          );
        } else {
          setError(gifmeErrorMessage(reason, "Cast could not be queued. Try again."));
        }
        return;
      }

      localPreviews.clear();
      setSelfieIntent(null);
      setSource(null);
      setStatus("Queued.");
      try {
        await Promise.all([refreshSelfies(), onQueued()]);
      } catch {
        setError("Cast was queued, but Studio could not refresh. Reload to see its latest state.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Panel title="New cast" icon="sparkles" className="studio-panel">
      <form className="studio-form" onSubmit={submit}>
        <div className="field-group">
          <Select
            label="Generation configuration"
            options={generationConfigs.map((config) => ({
              value: config.id,
              label: config.name,
            }))}
            value={selectedGenerationConfigId ?? ""}
            disabled={isLoadingGenerationConfigs || generationConfigs.length === 0}
            onChange={(id) => setSelectedGenerationConfigId(id)}
          />
          {generationConfigError ? (
            <Callout intent="danger" label="Could not load generation configurations">
              {generationConfigError}
              <Button
                type="button"
                intent="secondary"
                size="sm"
                onClick={() => void loadGenerationConfigs()}
              >
                Retry configurations
              </Button>
            </Callout>
          ) : null}
        </div>
        <div className="field-group">
          <Text kind="label" tone="secondary">
            Selfie
          </Text>
          <div className="thumbnail-grid">
            {selfies.map((saved) => (
              <SavedSelfieThumbnail
                key={saved.id}
                selfie={saved}
                cache={mediaCache}
                selected={selfie?.kind === "saved" && selfie.id === saved.id}
                onSelect={() => setSelfieIntent({ kind: "saved", id: saved.id })}
              />
            ))}
            {selfie?.kind === "upload" ? (
              <Thumbnail
                caption={selfie.file.name}
                src={selfie.previewUrl}
                selected
                onSelect={() => selfieInput.current?.click()}
              />
            ) : null}
            <div className="upload-tile">
              <Button
                type="button"
                intent="ghost"
                size="sm"
                icon="camera"
                shape="rounded"
                aria-label="Take a selfie with camera"
                disabled={isSubmitting}
                onClick={() => setIsCameraOpen(true)}
              />
            </div>
            <div className="upload-tile">
              <Button
                type="button"
                intent="ghost"
                size="sm"
                icon="upload"
                shape="rounded"
                aria-label="Upload a new selfie"
                onClick={() => selfieInput.current?.click()}
              />
              <input
                ref={selfieInput}
                className="visually-hidden"
                type="file"
                accept=".png,.jpg,.jpeg,.webp"
                aria-label="Upload a new selfie"
                onChange={uploadSelfie}
              />
            </div>
          </div>
        </div>

        <div className="field-group">
          <div className="section-label-row">
            <Text kind="label" tone="secondary">
              Input GIF
            </Text>
            <Button
              type="button"
              intent="ghost"
              size="sm"
              icon="layout-grid"
              onClick={() => {
                setIsPickerOpen(true);
              }}
            >
              Browse GIFs
            </Button>
          </div>
          <div className="source-slots">
            <SourceSlot
              choice={source}
              cache={mediaCache}
              onUpload={uploadSource}
              onBrowse={() => setIsPickerOpen(true)}
            />
          </div>
        </div>

        <Button
          type="submit"
          intent="positive"
          size="lg"
          icon="wand-2"
          loading={isSubmitting}
          disabled={!canSubmit}
        >
          Cast
        </Button>
        <Text as="p" kind="caption" tone="muted" role="status">
          {status}
        </Text>
        {error ? (
          <Callout intent="danger" label="Cast failed">
            {error}
          </Callout>
        ) : null}
      </form>
      {isCameraOpen ? (
        <CameraSelfieModal
          saveFile={saveCameraSelfie}
          onClose={() => setIsCameraOpen(false)}
          onSaved={() => {
            setIsCameraOpen(false);
            setError(null);
            toast.positive("Selfie saved");
          }}
        />
      ) : null}
      {isPickerOpen ? (
        <SourcePicker
          api={api}
          cache={mediaCache}
          onClose={() => setIsPickerOpen(false)}
          onSelect={chooseSource}
        />
      ) : null}
    </Panel>
  );
}
